import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { useIsCompanyAdmin } from "../../../hooks/useIsCompanyAdmin";
import {
  useCancelInvitationMutation,
  useUpdateUserMutation,
} from "../../../store/apiSlice/userApiSlice";
import { authUser } from "../../../store/slice/authSlice";
import { setNotification } from "../../../store/slice/miscSlice";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

const isInvitationRow = row =>
  row?.row_kind === "invitation" ||
  row?.status === "Invited" ||
  String(row?.id).startsWith("invitation:");

const invitationPk = row => {
  const raw = String(row?.id || "");
  return raw.startsWith("invitation:") ? raw.slice("invitation:".length) : raw;
};

/**
 * Selection actions for the Users list: Activate, Deactivate, Cancel invite.
 * Admins only; never toggles the signed-in user's own account.
 */
export const UsersListActions = ({
  selectedOrderFromListview = [],
  setSelectedOrder = () => {},
  onChanged,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(authUser);
  const isAdmin = useIsCompanyAdmin();
  const [updateUser, { isLoading: updating } = {}] = useUpdateUserMutation();
  const [cancelInvitation, { isLoading: cancelling } = {}] = useCancelInvitationMutation();
  const [confirm, setConfirm] = useState(null);

  const { activeUsers, inactiveUsers, invitedRows } = useMemo(() => {
    if (!isAdmin) {
      return { activeUsers: [], inactiveUsers: [], invitedRows: [] };
    }
    const selected = selectedOrderFromListview || [];
    const otherUsers = selected.filter(
      row => !isInvitationRow(row) && row?.id != null && row.id !== currentUser?.id,
    );
    return {
      activeUsers: otherUsers.filter(row => row?.is_active === true),
      inactiveUsers: otherUsers.filter(row => row?.is_active === false),
      invitedRows: selected.filter(isInvitationRow),
    };
  }, [currentUser?.id, isAdmin, selectedOrderFromListview]);

  const busy = updating || cancelling;

  const notifyError = err => {
    dispatch(
      setNotification({
        type: "error",
        message: err?.data?.detail || "Something went wrong.",
      }),
    );
  };

  const runActivate = async () => {
    try {
      await Promise.all(
        inactiveUsers.map(row => updateUser({ id: row.id, is_active: true }).unwrap()),
      );
      dispatch(
        setNotification({
          type: "success",
          message:
            inactiveUsers.length === 1
              ? "User activated."
              : `${inactiveUsers.length} users activated.`,
        }),
      );
      setSelectedOrder([]);
      onChanged?.();
    } catch (err) {
      notifyError(err);
    }
  };

  const runDeactivate = async () => {
    try {
      await Promise.all(
        activeUsers.map(row => updateUser({ id: row.id, is_active: false }).unwrap()),
      );
      dispatch(
        setNotification({
          type: "success",
          message:
            activeUsers.length === 1
              ? "User deactivated."
              : `${activeUsers.length} users deactivated.`,
        }),
      );
      setSelectedOrder([]);
      onChanged?.();
    } catch (err) {
      notifyError(err);
    }
  };

  const runCancelInvites = async () => {
    try {
      await Promise.all(invitedRows.map(row => cancelInvitation(invitationPk(row)).unwrap()));
      dispatch(
        setNotification({
          type: "success",
          message:
            invitedRows.length === 1
              ? "Invitation cancelled."
              : `${invitedRows.length} invitations cancelled.`,
        }),
      );
      setSelectedOrder([]);
      onChanged?.();
    } catch (err) {
      notifyError(err);
    }
  };

  const openConfirm = action => {
    if (action === "activate" && inactiveUsers.length) {
      setConfirm({
        titleOfModal: "Activate users",
        description:
          inactiveUsers.length === 1
            ? `Activate ${inactiveUsers[0].full_name || inactiveUsers[0].email}?`
            : `Activate ${inactiveUsers.length} selected users?`,
        btnName: "Activate",
        onConfirm: runActivate,
      });
    } else if (action === "deactivate" && activeUsers.length) {
      setConfirm({
        titleOfModal: "Deactivate users",
        description:
          activeUsers.length === 1
            ? `Deactivate ${activeUsers[0].full_name || activeUsers[0].email}?`
            : `Deactivate ${activeUsers.length} selected users?`,
        btnName: "Deactivate",
        onConfirm: runDeactivate,
      });
    } else if (action === "cancelInvite" && invitedRows.length) {
      setConfirm({
        titleOfModal: "Cancel invitations",
        description:
          invitedRows.length === 1
            ? `Cancel the invitation to ${invitedRows[0].email}? The signup link will stop working.`
            : `Cancel ${invitedRows.length} selected invitations? Signup links will stop working.`,
        btnName: "Cancel invite",
        onConfirm: runCancelInvites,
      });
    }
  };

  const canActivate = inactiveUsers.length > 0 && !busy;
  const canDeactivate = activeUsers.length > 0 && !busy;
  const canCancelInvite = invitedRows.length > 0 && !busy;

  const burgerItems = useMemo(
    () => [
      {
        key: "deactivate",
        label: "Deactivate",
        onClick: () => openConfirm("deactivate"),
        disabled: !canDeactivate,
        title: canDeactivate ? "" : "Select one or more active users to deactivate.",
      },
      {
        key: "cancel-invite",
        label: "Cancel invite",
        onClick: () => openConfirm("cancelInvite"),
        disabled: !canCancelInvite,
        title: canCancelInvite ? "" : "Select one or more invitations to cancel.",
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canDeactivate, canCancelInvite],
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <li>
        <button
          type="button"
          className={`tableButton ${canActivate ? "activeState" : ""}`}
          disabled={!canActivate}
          title={canActivate ? "" : "Select one or more inactive users to activate."}
          onClick={() => openConfirm("activate")}
        >
          Activate
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {confirm && (
        <ConfirmationModal
          closeModal={() => setConfirm(null)}
          titleOfModal={confirm.titleOfModal}
          description={confirm.description}
          btnName={confirm.btnName}
          isLoading={busy}
          handleModalWork={async () => {
            const fn = confirm.onConfirm;
            setConfirm(null);
            await fn();
          }}
        />
      )}
    </>
  );
};
